// 1.3.74: MVP 付费功能 - 计费服务
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { logger } from '../utils/logger';
import type {
  SubscriptionInfo,
  CreateCheckoutParams,
  CheckoutSessionResponse,
  PortalSessionResponse,
} from '../types/billing';

export interface ServiceError {
  message: string;
  code?: string;
}

// Edge Function URL
const EDGE_FUNCTION_URL = import.meta.env.VITE_SUPABASE_URL 
  ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
  : 'http://127.0.0.1:54421/functions/v1';

// ============================================
// 订阅状态查询
// ============================================

/**
 * 获取当前用户的订阅信息
 */
export async function getCurrentSubscription(): Promise<{
  subscription: SubscriptionInfo | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { subscription: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('get_current_user_subscription');

    if (error) {
      logger.error('Error getting subscription:', error);
      return {
        subscription: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { subscription: data as SubscriptionInfo, error: null };
  } catch (error: any) {
    logger.error('Error getting subscription:', error);
    return {
      subscription: null,
      error: { message: error.message || '获取订阅信息失败' },
    };
  }
}

/**
 * 获取指定组织的订阅信息
 */
export async function getOrganizationSubscription(
  organizationId: string
): Promise<{
  subscription: SubscriptionInfo | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { subscription: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data, error } = await supabase.rpc('get_organization_subscription', {
      org_id: organizationId,
    });

    if (error) {
      logger.error('Error getting organization subscription:', error);
      return {
        subscription: null,
        error: { message: error.message, code: error.code },
      };
    }

    return { subscription: data as SubscriptionInfo, error: null };
  } catch (error: any) {
    logger.error('Error getting organization subscription:', error);
    return {
      subscription: null,
      error: { message: error.message || '获取订阅信息失败' },
    };
  }
}

/**
 * 获取试用剩余天数
 */
export function getTrialDaysRemaining(subscription: SubscriptionInfo | null): number {
  if (!subscription?.trial_days_remaining) {
    return 0;
  }
  return Math.max(0, subscription.trial_days_remaining);
}

/**
 * 检查是否在试用期内
 * 1.3.90: 修复试用期检测逻辑 - 只要有剩余天数且非活跃订阅就算试用中
 * 1.6.57: 使用有效订阅状态判断
 */
export function isInTrial(subscription: SubscriptionInfo | null): boolean {
  if (!subscription) return false;
  
  // 如果有效状态是 active，不再是试用
  if (getEffectiveSubscriptionStatus(subscription) === 'active') {
    return false;
  }
  
  // 检查是否有剩余试用天数
  const daysRemaining = getTrialDaysRemaining(subscription);
  return daysRemaining > 0;
}

/**
 * 检查试用期是否已过期
 * 1.3.90: 改进逻辑 - 只有曾经有过试用期但已结束才算过期
 * 1.6.57: 使用有效订阅状态判断
 */
export function isTrialExpired(subscription: SubscriptionInfo | null): boolean {
  if (!subscription) return false;
  
  // 如果有效状态是 active，则试用期不算过期
  const effectiveStatus = getEffectiveSubscriptionStatus(subscription);
  if (effectiveStatus === 'active') {
    return false;
  }
  
  // 如果还在试用中，不算过期
  if (isInTrial(subscription)) {
    return false;
  }
  
  // 如果有过试用期（trial_ends_at 存在）但已结束，则过期
  // 或者状态是 none 且没有剩余天数
  if (subscription.trial_ends_at || 
      (effectiveStatus === 'none' && subscription.trial_days_remaining === 0)) {
    return true;
  }
  
  return false;
}

/**
 * 获取有效订阅状态
 * 1.6.57: 优先使用管理员覆盖状态，否则使用真实状态
 */
export function getEffectiveSubscriptionStatus(subscription: SubscriptionInfo | null): string {
  if (!subscription) return 'none';
  
  // 如果后端已经返回了有效状态，直接使用
  if (subscription.effective_subscription_status) {
    return subscription.effective_subscription_status;
  }
  
  // 检查管理员覆盖是否有效（前端备用逻辑）
  if (subscription.admin_override_subscription_status) {
    const overrideUntil = subscription.admin_override_until;
    if (!overrideUntil || new Date(overrideUntil) > new Date()) {
      return subscription.admin_override_subscription_status;
    }
  }
  
  return subscription.subscription_status || 'none';
}

/**
 * 检查是否有活跃订阅
 * 1.6.57: 使用有效订阅状态判断（支持管理员覆盖）
 */
export function hasActiveSubscription(subscription: SubscriptionInfo | null): boolean {
  return getEffectiveSubscriptionStatus(subscription) === 'active';
}

// ============================================
// Stripe Checkout 操作
// ============================================

/**
 * 创建 Checkout Session（跳转到 Stripe 支付页面）
 */
export async function createCheckoutSession(
  params: CreateCheckoutParams
): Promise<{
  result: CheckoutSessionResponse | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    // 获取当前用户 session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { result: null, error: { message: '请先登录' } };
    }

    // 获取当前语言设置
    const currentLocale = localStorage.getItem('i18nextLng') || navigator.language || 'auto';

    const response = await fetch(`${EDGE_FUNCTION_URL}/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: 'create_checkout_session',
        organizationId: params.organizationId,
        plan: params.plan,
        quantity: params.quantity || 1,
        locale: currentLocale,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        result: null,
        error: { message: data.error || '创建支付会话失败' },
      };
    }

    return { result: data as CheckoutSessionResponse, error: null };
  } catch (error: any) {
    logger.error('Error creating checkout session:', error);
    return {
      result: null,
      error: { message: error.message || '创建支付会话失败' },
    };
  }
}

/**
 * 创建 Customer Portal Session（跳转到 Stripe 订阅管理页面）
 */
export async function createPortalSession(
  organizationId: string
): Promise<{
  result: PortalSessionResponse | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    // 获取当前用户 session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { result: null, error: { message: '请先登录' } };
    }

    const response = await fetch(`${EDGE_FUNCTION_URL}/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: 'create_portal_session',
        organizationId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        result: null,
        error: { message: data.error || '创建管理门户失败' },
      };
    }

    return { result: data as PortalSessionResponse, error: null };
  } catch (error: any) {
    logger.error('Error creating portal session:', error);
    return {
      result: null,
      error: { message: error.message || '创建管理门户失败' },
    };
  }
}

// ============================================
// 便捷函数
// ============================================

/**
 * 跳转到 Stripe Checkout 页面
 */
export async function redirectToCheckout(params: CreateCheckoutParams): Promise<ServiceError | null> {
  const { result, error } = await createCheckoutSession(params);
  
  if (error) {
    return error;
  }
  
  if (result?.url) {
    window.location.href = result.url;
    return null;
  }
  
  return { message: '无法获取支付页面链接' };
}

/**
 * 更新 Standard 套餐人数
 * 增加人数：立即按比例收费
 * 减少人数：下个周期生效
 */
export async function updateSubscriptionQuantity(
  organizationId: string,
  newQuantity: number
): Promise<{
  result: {
    success: boolean;
    previousQuantity: number;
    newQuantity: number;
    isUpgrade: boolean;
    message: string;
    nextPeriodEnd: string;
  } | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { result: null, error: { message: '请先登录' } };
    }

    const response = await fetch(`${EDGE_FUNCTION_URL}/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: 'update_quantity',
        organizationId,
        quantity: newQuantity,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        result: null,
        error: { message: data.error || '更新人数失败' },
      };
    }

    return { result: data, error: null };
  } catch (error: any) {
    logger.error('Error updating subscription quantity:', error);
    return {
      result: null,
      error: { message: error.message || '更新人数失败' },
    };
  }
}

/**
 * 1.3.81: 恢复已取消的订阅
 * 当订阅设置为"周期结束后取消"时，可以恢复订阅使其继续自动续费
 */
export async function resumeSubscription(
  organizationId: string
): Promise<{
  result: {
    success: boolean;
    message: string;
    nextPeriodEnd: string;
  } | null;
  error: ServiceError | null;
}> {
  if (!isSupabaseAvailable) {
    return { result: null, error: { message: 'Supabase not available' } };
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { result: null, error: { message: '请先登录' } };
    }

    const response = await fetch(`${EDGE_FUNCTION_URL}/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: 'resume_subscription',
        organizationId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        result: null,
        error: { message: data.error || '恢复订阅失败' },
      };
    }

    return { result: data, error: null };
  } catch (error: any) {
    logger.error('Error resuming subscription:', error);
    return {
      result: null,
      error: { message: error.message || '恢复订阅失败' },
    };
  }
}

/**
 * 打开 Stripe Customer Portal（新 tab）
 * 1.3.79: 改为在新标签页中打开
 */
export async function redirectToPortal(organizationId: string): Promise<ServiceError | null> {
  const { result, error } = await createPortalSession(organizationId);
  
  if (error) {
    return error;
  }
  
  if (result?.url) {
    // 1.3.79: 在新标签页中打开 Stripe 管理门户
    window.open(result.url, '_blank', 'noopener,noreferrer');
    return null;
  }
  
  return { message: '无法获取管理门户链接' };
}

/**
 * 格式化价格显示
 */
export function formatPrice(price: number, perUser: boolean = false): string {
  const formatted = new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
  
  return perUser ? `${formatted}/人/月` : `${formatted}/月`;
}

/**
 * 格式化日期显示
 */
export function formatDate(dateString: string | undefined): string {
  if (!dateString) return '-';
  
  try {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
}

/**
 * 格式化订阅周期
 */
export function formatSubscriptionPeriod(
  start: string | undefined,
  end: string | undefined
): string {
  if (!start || !end) return '-';
  return `${formatDate(start)} 至 ${formatDate(end)}`;
}
